/**
 * Topology Types Module
 * TypeScript interfaces for the 5-level Bank Service Topology
 *
 * Sprint 33: Following OOA/OOD principles:
 * - Domain Modeling: Reflects real-world bank hierarchy
 * - Interface Segregation: Separate interfaces for each concern
 *
 * Hierarchy:
 * Level 1: Bank (implicit root)
 * Level 2: Business Unit
 * Level 3: Business Capability
 * Level 4: Platform
 * Level 5: Process Flow
 */

// ======================
// Topology Node Types
// ======================

export type TopologyLevel = 1 | 2 | 3 | 4 | 5;

export type TopologyNodeType =
    | 'bank'
    | 'business-unit'
    | 'capability'
    | 'platform'
    | 'process-flow'
    | 'application'
    | 'infrastructure';

export interface TopologyNode {
    id: string;
    label: string;
    level: TopologyLevel;
    type: TopologyNodeType;
    children: TopologyNode[];
    metadata: Record<string, string>;
    expanded: boolean;
}

// ======================
// Process Flow Types (Level 5)
// ======================

export type ProcessStepType =
    | 'HumanAction'
    | 'SystemAction'
    | 'Decision'
    | 'Integration'
    | 'Notification'
    | 'ManualReview';

export type ProcessFlowStatus =
    | 'Draft'
    | 'Active'
    | 'Archived';

export interface ProcessFlowData {
    processFlowId: string;
    name: string;
    description: string;
    businessUnitId: string;
    capabilityId: string;
    platformId?: string;
    status: ProcessFlowStatus;
    ownerRole: string;
    totalSlaHours?: number;
    metadata: Record<string, string>;
}

export interface ProcessStepData {
    stepId: string;
    processFlowId: string;
    stepNumber: number;
    name: string;
    description: string;
    stepType: ProcessStepType;
    role: string;
    system?: string;
    infrastructure: string[];
    slaHours?: number;
    nextSteps: string[];
    decisionOptions: Array<[string, string]>;
    metadata: Record<string, string>;
}

// ======================
// Visualization Types
// ======================

export interface TopologyRenderOptions {
    width: number;
    height: number;
    nodeRadius: number;
    levelColors: Record<TopologyLevel, string>;
    animationDuration: number;
}

export const DEFAULT_LEVEL_COLORS: Record<TopologyLevel, string> = {
    1: '#1e40af', // Bank - deep blue
    2: '#7c3aed', // Business Unit - purple
    3: '#059669', // Capability - green
    4: '#d97706', // Platform - amber
    5: '#dc2626', // Process Flow - red
};

export const STEP_TYPE_COLORS: Record<ProcessStepType, string> = {
    HumanAction: '#3b82f6',
    SystemAction: '#10b981',
    Decision: '#f59e0b',
    Integration: '#8b5cf6',
    Notification: '#06b6d4',
    ManualReview: '#f97316',
};

export const STEP_TYPE_ICONS: Record<ProcessStepType, string> = {
    HumanAction: 'user',
    SystemAction: 'cpu',
    Decision: 'git-branch',
    Integration: 'refresh-cw',
    Notification: 'bell',
    ManualReview: 'eye',
};

// ======================
// Node Icon SVG Paths (24x24 viewBox)
// ======================

/** Default SVG path per topology node type */
export const NODE_TYPE_ICONS: Record<TopologyNodeType, string> = {
    'bank': 'M3 21h18v-2H3v2zm0-4h18v-1H3v1zm1-5l8-8 8 8H4zm8-5.5L6.5 12h11L12 6.5z', // Bank building
    'business-unit': 'M20 7h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v3H4c-1.1 0-2 .9-2 2v11h20V9c0-1.1-.9-2-2-2zM10 4h4v3h-4V4zm10 15H4v-9h16v9z', // Briefcase
    'capability': 'M19.14 12.94a7.07 7.07 0 000-1.88l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.49.49 0 00-.59-.22l-2.39.96a7.04 7.04 0 00-1.63-.94l-.36-2.54a.48.48 0 00-.48-.41h-3.84a.48.48 0 00-.48.41l-.36 2.54c-.59.22-1.13.53-1.63.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87a.48.48 0 00.12.61l2.03 1.58a7.07 7.07 0 000 1.88l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.41 1.04.72 1.63.94l.36 2.54c.05.24.26.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54c.59-.22 1.13-.53 1.63-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 00-.12-.61l-2.03-1.58zM12 15.6A3.6 3.6 0 1115.6 12 3.6 3.6 0 0112 15.6z', // Gear/cog
    'platform': 'M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z', // Layers/stack
    'process-flow': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z', // Flow/check
    'application': 'M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z', // Grid/app icon
    'infrastructure': 'M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zM7 19c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM20 3H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM7 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z', // Server rack
};

/** Keyword-based icon overrides (matched against node label, case-insensitive) */
export const NODE_LABEL_ICONS: Record<string, string> = {
    // Bank / root
    'bank': 'M3 21h18v-2H3v2zm0-4h18v-1H3v1zm1-5l8-8 8 8H4zm8-5.5L6.5 12h11L12 6.5z',
    // Head Office - classical columns
    'head office': 'M6 20h2v-4h2v4h4v-4h2v4h2V10L12 3 6 10v10zm6-10c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z',
    // Customer Management - people
    'customer': 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
    // Retail Banking - credit card
    'retail': 'M20 4H4c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z',
    // IT Operations - monitor
    'it operations': 'M21 2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7v2H8v2h8v-2h-2v-2h7c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H3V4h18v12z',
    // International - globe
    'international': 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95a15.65 15.65 0 00-1.38-3.56A8.03 8.03 0 0118.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.987 7.987 0 015.08 16zm2.95-8H5.08a7.987 7.987 0 014.33-3.56A15.65 15.65 0 008.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 01-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z',
    // Mortgage - house
    'mortgage': 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
    // Business Banking - briefcase
    'business': 'M20 7h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v3H4c-1.1 0-2 .9-2 2v11h20V9c0-1.1-.9-2-2-2zM10 4h4v3h-4V4zm10 15H4v-9h16v9z',

    // === Capability Icons ===

    // Sales - trending up chart
    'sales': 'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    // Processing - cycle/sync arrows
    'processing': 'M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z',
    // Help Desk - headset
    'help': 'M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z',
    // Portal - web browser window
    'portal': 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10zM4 6h16v1H4V6zm1 .5a.5.5 0 110-1 .5.5 0 010 1zm2 0a.5.5 0 110-1 .5.5 0 010 1zm2 0a.5.5 0 110-1 .5.5 0 010 1z',
    // HR / Human Resources - people with plus
    'hr': 'M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
    'human resource': 'M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
    // Admin - shield with settings
    'admin': 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z',
    // Compliance - clipboard with check
    'compliance': 'M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
    // Operations - wrench tool
    'operations': 'M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z',
    // Risk - warning triangle
    'risk': 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
    // Wealth - diamond/gem
    'wealth': 'M19 3H5L2 9l10 12L22 9l-3-6zm-1.18 5h-3.5l1.75-3.5L17.82 8zM12 15.5L6.18 8h11.64L12 15.5zM7.93 8H4.43l1.75-3.5L7.93 8zm2.32-3.5h3.5L12 8 10.25 4.5z',
    // Corporate - office building
    'corporate': 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z',
};

// ======================
// Canister Request/Response DTOs
// ======================

export interface ProcessFlowStorageRequest {
    process_flow_id: string;
    name: string;
    description: string;
    business_unit_id: string;
    capability_id: string;
    platform_id: string[] | [];  // Candid opt
    status: string[] | [];       // Candid opt
    owner_role: string;
    total_sla_hours: number[] | [];  // Candid opt
    metadata: Array<[string, string]>[] | [];  // Candid opt
}

export interface ProcessStepStorageRequest {
    step_id: string;
    process_flow_id: string;
    step_number: number;
    name: string;
    description: string;
    step_type: { [key: string]: null };  // Candid variant
    role: string;
    system: string[] | [];
    infrastructure: string[][] | [];
    sla_hours: number[] | [];
    next_steps: string[][] | [];
    decision_options: Array<[string, string]>[] | [];
    metadata: Array<[string, string]>[] | [];
}

export interface BulkProcessFlowImport {
    flows: ProcessFlowStorageRequest[];
    steps: ProcessStepStorageRequest[];
}

export interface ProcessFlowStatistics {
    total_flows: number;
    total_steps: number;
    flows_by_status: Array<[string, number]>;
    flows_by_business_unit: Array<[string, number]>;
    avg_steps_per_flow: number;
}
